import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../Button/Button";
import Input from "../Input/Input";
import Divider from "../Divider/Divider";
import Checkbox from "../Checkbox/Checkbox";
import { useAuth } from "../../context/AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/config";

// Utility to generate Secret Santa assignments
function assignSecretSanta(participants) {
    if (!participants || participants.length < 2) return participants;

    const shuffled = [...participants].sort(() => Math.random() - 0.5);

    return shuffled.map((p, i) => ({
        ...p,
        assignedTo: shuffled[(i + 1) % shuffled.length].name, // assign by name
    }));
}

export default function AddListForm({ addList, editList, editListFn, onClose }) {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [creatorName, setCreatorName] = useState("");
    const [formData, setFormData] = useState({
        name: "",
        isSecretSanta: false,
        participants: [{ name: "" }],
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const fetchCreatorName = async () => {
            if (!user) return;
            try {
                const userRef = doc(db, "users", user.uid);
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                    const data = userSnap.data();
                    setCreatorName(`${data.firstName} ${data.lastName}`);
                } else {
                    setCreatorName(user.displayName || "Creator");
                }
            } catch (err) {
                console.error("Error fetching creator name:", err);
                setCreatorName(user.displayName || "Creator");
            }
        };
        fetchCreatorName();
    }, [user]);

    useEffect(() => {
        if (editList) {
            setFormData({
                name: editList.name || "",
                isSecretSanta: editList.isSecretSanta || false,
                participants: editList.participants?.length
                    ? editList.participants.filter(p => p.name !== creatorName)
                    : [{ name: "" }],
            });
        }
    }, [editList, creatorName]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleAddParticipant = () => {
        setFormData((prev) => ({
            ...prev,
            participants: [...prev.participants, { name: "" }],
        }));
    };

    const handleRemoveParticipant = (index) => {
        setFormData((prev) => {
            const updated = [...prev.participants];
            updated.splice(index, 1);
            return { ...prev, participants: updated };
        });
    };

    const handleParticipantChange = (index, value) => {
        setFormData((prev) => {
            const updated = [...prev.participants];
            updated[index].name = value;
            return { ...prev, participants: updated };
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "List name is required";

        if (formData.isSecretSanta) {
            const emptyNames = formData.participants.some(
                (p) => !p.name.trim()
            );
            if (emptyNames)
                newErrors.participants = "All participant names are required";
        }

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        let allParticipants = [
            {
                name:
                    user.firstName && user.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user.displayName || "You",
                userId: user.uid,
                assignedTo: null,
            },
            ...formData.participants
                .filter((p) => p.name.trim())
                .map((p) => ({ name: p.name, userId: null, assignedTo: null })),
        ];

        if (formData.isSecretSanta) {
            allParticipants = assignSecretSanta(allParticipants);
        }

        const newListData = {
            ...formData,
            ownerId: user.uid,
            members: [user.uid],
            items: [],
            memberInfo: [
                {
                    uid: user.uid,
                    displayName:
                        user.firstName && user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user.displayName || "Member",
                },
            ],
            participants: allParticipants,
        };

        if (editList && editListFn) {
            editListFn({ ...editList, ...newListData });
        } else if (addList) {
            addList(newListData);
        }

        if (onClose) onClose();
        navigate("/");
    };

    return (
        <div>
            <h1 className="text-xl font-bold mb-2">
                {editList ? "Edit List" : "Add List"}
            </h1>

            <Divider />

            <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                    label="List Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={errors.name}
                    required
                />

                <Checkbox
                    label="This is a Secret Santa list"
                    name="isSecretSanta"
                    checked={formData.isSecretSanta}
                    onChange={(checked) =>
                        setFormData((prev) => ({ ...prev, isSecretSanta: checked }))
                    }
                />

                {formData.isSecretSanta && (
                    <div className="flex flex-col gap-2 p-2 border border-surface0 rounded-md">
                        <h4 className="font-semibold text-sm mb-1">Participants</h4>

                        <Input
                            value={
                                user.firstName && user.lastName
                                    ? `${user.firstName} ${user.lastName}`
                                    : user.displayName || "You"
                            }
                            disabled
                        />

                        {formData.participants.map((participant, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <Input
                                    value={participant.name}
                                    onChange={(e) =>
                                        handleParticipantChange(index, e.target.value)
                                    }
                                    placeholder={`Participant ${index + 1}`}
                                />
                                {formData.participants.length > 1 && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveParticipant(index)}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </div>
                        ))}

                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleAddParticipant}
                            className="mt-1"
                        >
                            + Add Participant
                        </Button>

                        {errors.participants && (
                            <p className="text-red-500 text-sm">{errors.participants}</p>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-4">
                    {onClose && (
                        <Button type="button" onClick={onClose} variant="ghost">
                            Cancel
                        </Button>
                    )}
                    <Button type="submit" variant="primary">
                        {editList ? "Update List" : "Add List"}
                    </Button>
                </div>
            </form>
        </div>
    );
}
